import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue";
import { computed, ref } from "vue";
import ChartPagePickerDialog from "@/components/ChartPagePickerDialog.vue";
import DocumentUploadList from "@/components/DocumentUploadList.vue";
import { orderInfoService } from "@/services/orderInfoService";
import { useOrderDocumentRows } from "@/composables/useOrderDocumentRows";
import { usePreparedInfo } from "@/composables/usePreparedInfo";
import { useDocumentLink } from "@/composables/useDocumentLink";
import { useRequirementsStore } from "@/stores/requirementsStore";
/** A draft order's document list: saved payer files, AI-only files and uploads in flight, in one list. */
export default /*@__PURE__*/ _defineComponent({
    __name: 'OrderDocumentList',
    props: {
        patientChart: { default: () => [] },
        aiReferenceDocuments: { default: () => [] },
        medicalNecessity: { default: () => [] },
        isEditable: { type: Boolean, default: false },
        cmmBudgeted: { type: Boolean, default: false },
        orderKey: { default: null },
        formButtonClicked: { type: Boolean, default: false },
        patientChartNotNeeded: { type: Boolean, default: false }
    },
    emits: ["add-uploaded-file", "delete-uploaded-file", "update-ai-reference-documents"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const { remember, sizeOf, factsOf } = usePreparedInfo();
        const { openDocument } = useDocumentLink();
        const requirementsStore = useRequirementsStore();
        const openLink = (doc) => {
            const [link] = doc.links;
            if (doc.saved && props.orderKey)
                void openDocument(props.orderKey, link);
            else
                window.open(link, "_blank", "noopener");
        };
        const { rows, notice, budgetCaption, isUploading, addFiles, uploadInto, remove, setSendToPayer, updateLabel, canChoosePages, pickerProps, openPicker, closePicker, confirmPicker, } = useOrderDocumentRows({
            patientChart: () => props.patientChart,
            aiReferenceDocuments: () => props.aiReferenceDocuments,
            otherBudgetLinks: () => props.medicalNecessity ?? [],
            knownSize: sizeOf,
            cmmBudgeted: () => props.cmmBudgeted,
            addDocument: (change) => {
                if (change.link && change.sizeBytes != null)
                    remember(change.link, { sizeBytes: change.sizeBytes });
                emit("add-uploaded-file", change);
            },
            deleteDocument: (change) => emit("delete-uploaded-file", change),
            setReferences: (references, removedLinks) => emit("update-ai-reference-documents", references, removedLinks),
            documentBuckets: () => requirementsStore.clientConfig?.documentBuckets ?? [],
            knownFacts: factsOf,
            rememberPrepared: remember,
        });
        const payerDocsUploaded = computed(() => (props.patientChart ?? []).some((doc) => (doc.links ?? []).length > 0));
        const aiOnlyDocsUploaded = computed(() => (props.aiReferenceDocuments ?? []).some((doc) => (doc.links ?? []).length > 0));
        const mergingPdf = ref(false);
        const openMergedPdf = async () => {
            if (!props.orderKey)
                return;
            mergingPdf.value = true;
            try {
                const response = await orderInfoService.getSupportingDocumentsPdf(props.orderKey);
                const pdfUrl = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
                window.open(pdfUrl, "_blank");
                setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
            }
            catch {
                notice.value = "Could not open the documents sent to the payer. Please try again.";
            }
            finally {
                mergingPdf.value = false;
            }
        };
        __expose({ isUploading });
        return (_ctx, _cache) => {
            const _component_v_alert = _resolveComponent("v-alert");
            const _component_v_btn = _resolveComponent("v-btn");
            return (_openBlock(), _createElementBlock("div", null, [
                (_unref(pickerProps))
                    ? (_openBlock(), _createBlock(ChartPagePickerDialog, {
                        key: 0,
                        "model-value": true,
                        "download-url": _unref(pickerProps).downloadUrl,
                        "per-page-bytes": _unref(pickerProps).perPageBytes,
                        "budget-bytes": _unref(pickerProps).budgetBytes,
                        "allow-over-budget": _unref(pickerProps).allowOverBudget,
                        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => (!$event && _unref(closePicker)())),
                        onConfirm: _unref(confirmPicker)
                    }, null, 8, ["download-url", "per-page-bytes", "budget-bytes", "allow-over-budget", "onConfirm"]))
                    : _createCommentVNode("", true),
                _createVNode(DocumentUploadList, {
                    docs: _unref(rows),
                    readonly: !__props.isEditable,
                    "budget-caption": _unref(budgetCaption),
                    "can-choose-pages": _unref(canChoosePages),
                    "open-link": openLink,
                    onAddFiles: _unref(addFiles),
                    onUploadInto: _unref(uploadInto),
                    onRemove: _unref(remove),
                    onUpdateSendToPayer: _unref(setSendToPayer),
                    onUpdateLabel: _unref(updateLabel),
                    onChoosePages: _unref(openPicker)
                }, null, 8, ["docs", "readonly", "budget-caption", "can-choose-pages", "onAddFiles", "onUploadInto", "onRemove", "onUpdateSendToPayer", "onUpdateLabel", "onChoosePages"]),
                (_unref(notice))
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 1,
                        type: "warning",
                        variant: "tonal",
                        density: "compact",
                        class: "mt-2"
                    }, {
                        default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(notice)), 1)
                        ]),
                        _: 1
                    }))
                    : _createCommentVNode("", true),
                (__props.formButtonClicked && !payerDocsUploaded.value && !aiOnlyDocsUploaded.value && !__props.patientChartNotNeeded)
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 2,
                        type: "error",
                        variant: "tonal",
                        density: "compact",
                        class: "mt-3 form-validation-alert"
                    }, {
                        default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                                _createTextVNode(" Add at least one document before you continue. ", -1)
                            ]))]),
                        _: 1
                    }))
                    : _createCommentVNode("", true),
                (__props.orderKey && payerDocsUploaded.value)
                    ? (_openBlock(), _createBlock(_component_v_btn, {
                        key: 3,
                        loading: mergingPdf.value,
                        color: "primary",
                        variant: "text",
                        "prepend-icon": "mdi-file-pdf-box",
                        class: "mt-2 px-0",
                        onClick: openMergedPdf
                    }, {
                        default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                                _createTextVNode(" View documents sent to the payer ", -1)
                            ]))]),
                        _: 1
                    }, 8, ["loading"]))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
